/**
 * Media Library Component
 *
 * A comprehensive media management component that provides both media type selection
 * and asset browsing capabilities with support for multiple asset providers.
 */

import { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle, lazy, Suspense, type ForwardedRef } from 'react';
import { createPortal } from 'react-dom';
import { Client, ILabel, ZmlPermissions, WorkspaceUserPermissions } from '@zappar/zw-api-client';
import type { IAsset } from './providers';
import { ProviderRegistry, ZapworksProvider } from './providers';
import { MediaLibraryMode, MediaLibraryView, MediaType, AssetSelectCallback, AssetSource, MediaLibraryTheme, SortOption, Filters } from './types';
import { MediaTypeSelection, AssetBrowserView, HomeView } from './components';
import './styles/index.scss';
import { useUploadQueue, useDeleteAssets, useWorkflowStatuses, useMediaLibraryUrlState } from './hooks';
import type { UrlNavState } from './hooks';
import { ExploreView } from './components/ExploreView';
import { ModeProvider } from './context/ModeContext';
import { ThemeProvider } from './context/ThemeContext';
import { AssetActionsProvider } from './context/AssetActionsContext';
import {
  labelsAddedBody,
  labelsRemovedBody,
  labelsChangedBody,
  workflowStatusChangedBody,
  restoredFromBinBody,
  versionUploadedBody,
  movedToBinBody,
  type SystemMessage,
} from './echo/systemMessages';
import { EchoProvider, useEchoContext } from './echo/EchoContext';
import { ZprToasts } from '@zpr-stencil';
import type { Components, ToastType } from '@zappar/zpr-ui-components-stencil';
import { AssetView } from './components/AssetView';
import { UploadPopup } from './components/UploadPopup';
import { GltfMissingFilesView } from './components/GltfMissingFilesView';
import { Uploader } from '../UI/Uploader';
import { mediaLibraryActions, mediaLibraryStore } from './mediaLibraryStore';

const AnalyticsView = lazy(() =>
  import('./components/AnalyticsView').then(m => ({ default: m.AnalyticsView }))
);

export interface MediaLibraryProps {
  /** Zapworks API client for accessing user's media library */
  zwApiClient: Client;
  /** Available media types to display in the selection view */
  enabledMediaTypes?: MediaType[];
  /** Initial media type to show (skips selection view if provided) */
  initialMediaType?: MediaType;
  /** Initial asset source to show (skips selection view if provided) */
  initialSource?: AssetSource;
  /** Workspace ID for filtering ZML assets */
  workspaceId: string;
  /**
   * Operation mode of the media library
   * - tool: Navigation enabled, asset type selection enabled (default)
   * - cms: Navigation disabled, mixed asset types, ZapworksProvider only
   */
  mode?: MediaLibraryMode;
  /** Enable premium features (default: false) */
  premium?: boolean;
  /**
   * Nestle specific premium features (default: false)
   * - Request asset button in AssetView
   * - ...any other Nestle-specific features that may be added in the future
   */
  useNestleFeatures?: boolean;
  /** Whether multi-selection can be enabled initially and via the header toggle (default: true) */
  initialEnableMultiSelect?: boolean;
  /** Freesound API key for accessing Freesound audio library (optional) */
  freesoundApiKey?: string;
  /** Callback when an asset is selected */
  onSelect?: AssetSelectCallback;
  /** Callback for adding asset to project (if supported by provider) */
  onAddToProject?: (asset: IAsset) => void;
  /** DOM element to portal the Uploader widget into (provider and queue are managed internally) */
  uploaderContainer?: HTMLElement | null;
  /** Theme for the media library (default: 'plain') */
  theme?: MediaLibraryTheme;
  /** Custom CSS class for styling */
  className?: string;
  /**
   * When true, the current view/source/type/search is reflected in the URL hash
   * (params are prefixed with "zml-") enabling back/forward navigation and deep linking.
   * Only useful when the component is rendered as a full-page experience.
   * Default: false
   */
  enableUrlNavigation?: boolean;
  environmentImageUrl?: string;
}

type MediaLibraryRef = { openMissingFilesDialog: (uploadId: string) => void };

const MediaLibraryInner = ({
  zwApiClient,
  enabledMediaTypes,
  initialMediaType,
  initialSource,
  workspaceId,
  mode = 'tool',
  premium = false,
  useNestleFeatures = false,
  initialEnableMultiSelect = false,
  freesoundApiKey,
  onSelect,
  onAddToProject,
  uploaderContainer,
  className = '',
  theme = 'plain',
  enableUrlNavigation = false,
  environmentImageUrl,
  _forwardedRef,
}: MediaLibraryProps & { _forwardedRef: ForwardedRef<MediaLibraryRef> }) => {
  if (!enabledMediaTypes || enabledMediaTypes.length === 0) {
    enabledMediaTypes = Object.values(MediaType);
  }

  // Create provider registry with all known providers
  const registry = useMemo(() => {
    const reg = new ProviderRegistry();

    // Register Zapworks provider (supports read/write/delete operations)
    // This provider powers:
    // - "My assets" (user-uploaded, read/write/delete)
    // - "Workspace assets" (workspace-shared, read/write/delete)
    // - "Bin" (trash bin, read/restore)
    // - "Zappar library" (access='public', read-only, limited to Image/Video/Audio/Model3D)
    reg.register(new ZapworksProvider(zwApiClient));

    // If we are in CMS mode, we don't register external providers
    if (mode !== 'tool') {
      return reg;
    }

    // // Register external read-only providers
    // // These appear in the "Explore" section and are filtered by supported media types
    // reg.register(new PolyHavenProvider());

    // // Register Freesound if API key is provided
    // if (freesoundApiKey) {
    //   reg.register(new FreesoundProvider({ apiKey: freesoundApiKey }));
    // }

    // TODO: Register other providers as they become available:
    // - PolyPizza (3D models)
    // - Pixabay (images/videos)

    return reg;
  }, [zwApiClient, freesoundApiKey, mode]);

  // ─── URL navigation (read initial state once before setting up state) ───────
  // We read the initial URL state synchronously before useState calls so the
  // initial values can be derived from the URL when enableUrlNavigation is true.
  const getInitialView = (): MediaLibraryView => {
    if (enableUrlNavigation) {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      const view = params.get('zml-view');
      if (view && ['home', 'browser', 'explore', 'asset', 'analytics', 'selection'].includes(view)) {
        return view as MediaLibraryView;
      }
    }
    return initialMediaType ? 'browser' : 'home';
  };

  const getInitialSource = (): AssetSource => {
    if (enableUrlNavigation) {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      const source = params.get('zml-source');
      if (source) return source as AssetSource;
    }
    return 'workspace-library';
  };

  const getInitialMediaType = (): MediaType | null => {
    if (enableUrlNavigation) {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      const type = params.get('zml-type');
      if (type && Object.values(MediaType).includes(type as MediaType)) {
        return type as MediaType;
      }
    }
    return initialMediaType || null;
  };

  const getInitialSearchQuery = (): string => {
    if (enableUrlNavigation) {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      return params.get('zml-q') ?? '';
    }
    return '';
  };

  // Both modes start with 'home' view (unless initialMediaType is provided)
  const [currentView, setCurrentView] = useState<MediaLibraryView>(getInitialView);
  const [selectedMediaType, setSelectedMediaType] = useState<MediaType | null>(getInitialMediaType);
  // Track active source for navigation from home view
  const [activeSource, setActiveSource] = useState<AssetSource>(getInitialSource);

  const [selectedAsset, setSelectedAsset] = useState<IAsset | null>(null); // For asset detail view

  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [uploadPopupFiles, setUploadPopupFiles] = useState<File[]>([]);
  const [activeGltfUploadId, setActiveGltfUploadId] = useState<string | null>(null);
  const [zmlPermission, setZmlPermission] = useState<WorkspaceUserPermissions['zml']>(undefined);

  // Shared search query across views
  const [searchQuery, setSearchQuery] = useState<string>(getInitialSearchQuery);
  const [enableMultiSelect, setEnableMultiSelect] = useState<boolean>(initialEnableMultiSelect);

  // Shared sort and filters — lifted so they persist when switching between views
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');
  const [filters, setFilters] = useState<Filters>({
    animated: [],
    templateType: [],
    status: [],
    labels: [],
    categories: [],
  });

  // Incremented to force child views to re-fetch assets (e.g. after delete/upload)
  const [refreshKey, setRefreshKey] = useState(0);

  // If only one media type is enabled in tool mode, skip home view and go directly to browser.
  // Skip when URL navigation is enabled and the URL already encodes a view (the lazy useState
  // initialisers have already read the correct initial values from the hash).
  const hasUrlView = enableUrlNavigation && !!new URLSearchParams(
    window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash
  ).get('zml-view');
  useEffect(() => {
    if (hasUrlView) return;
    if (mode === 'tool' && enabledMediaTypes.length === 1) {
      setSelectedMediaType(enabledMediaTypes[0]);
      setCurrentView('browser');
    }
  }, [enabledMediaTypes, mode, hasUrlView]);

  // Opens direct to Workspace tab, 'home' is skipped in the CMS, even if personal assets exist.
  // Skip when URL navigation is enabled and the URL already encodes the view/source.
  useEffect(() => {
    if (hasUrlView) return;
    if (mode === 'cms') {
      if (initialSource) {
        setActiveSource(initialSource);
      } else {
        setActiveSource('workspace-library');
      }
      setCurrentView('browser');
    }
  }, [mode, initialSource, hasUrlView]);

  useEffect(() => {
    if (mode !== 'cms') return;

    const handleNavigate = (e: Event) => {
      const customEvent = e as CustomEvent<AssetSource>;
      if (customEvent.detail) {
        handleSourceSelect(customEvent.detail);
      }
    };

    window.addEventListener('zml:navigate', handleNavigate);
    return () => {
      window.removeEventListener('zml:navigate', handleNavigate);
    };
  }, [mode]);

  // ─── URL navigation hook ─────────────────────────────────────────────────
  const { pushUrlState } = useMediaLibraryUrlState(enableUrlNavigation, {
    onPopState: useCallback((state: Partial<UrlNavState>) => {
      // Restore state when the user navigates back/forward
      if (state.view) setCurrentView(state.view);
      if (state.source !== undefined) setActiveSource(state.source);
      if (state.mediaType !== undefined) setSelectedMediaType(state.mediaType);
      if (state.searchQuery !== undefined) setSearchQuery(state.searchQuery);
      // For asset/analytics views, fetch the asset if we have its ID
      if ((state.view === 'asset' || state.view === 'analytics') && state.assetId) {
        const provider = registry.get('zapworks') as ZapworksProvider | undefined;
        provider?.getAssetById(state.assetId, workspaceId).then(asset => {
          if (asset) setSelectedAsset(asset);
        }).catch(() => { /* ignore – asset may no longer exist */ });
      }
      // Clear selected asset when navigating away from asset views
      if (state.view && state.view !== 'asset' && state.view !== 'analytics') {
        setSelectedAsset(null);
      }
    }, [registry, workspaceId]),
  });

  // Tracks whether we are currently fetching the asset referenced in the URL on initial load.
  // Must be declared before the URL sync effect that reads it.
  const didFetchInitialAsset = useRef(false);
  const [isFetchingInitialAsset, setIsFetchingInitialAsset] = useState(() => {
    if (!enableUrlNavigation) return false;
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const view = params.get('zml-view');
    const id = params.get('zml-id');
    return (view === 'asset' || view === 'analytics') && !!id;
  });

  // Sync URL whenever the key navigation state changes.
  // Skip while the initial asset is being fetched to avoid overwriting the URL
  // (and losing zml-id) before selectedAsset has been populated.
  useEffect(() => {
    if (!enableUrlNavigation) return;
    if (isFetchingInitialAsset) return;
    // Transient views (gltf-missing-files) are not written to the URL
    if (currentView === 'gltf-missing-files') return;
    pushUrlState({
      view: currentView,
      source: activeSource,
      mediaType: selectedMediaType ?? undefined,
      assetId: selectedAsset?.getId() ?? undefined,
      searchQuery: searchQuery || undefined,
    });
  }, [enableUrlNavigation, isFetchingInitialAsset, currentView, activeSource, selectedMediaType, selectedAsset, searchQuery, pushUrlState]);

  // If we deep-linked directly to an asset view, fetch the asset from the URL id
  useEffect(() => {
    if (!enableUrlNavigation || didFetchInitialAsset.current) return;
    if ((currentView === 'asset' || currentView === 'analytics') && !selectedAsset) {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      const id = params.get('zml-id');
      if (id) {
        didFetchInitialAsset.current = true;
        const provider = registry.get('zapworks') as ZapworksProvider | undefined;
        provider?.getAssetById(id, workspaceId).then(asset => {
          if (asset) setSelectedAsset(asset);
          else setCurrentView('home');
        }).catch(() => setCurrentView('home'))
          .finally(() => setIsFetchingInitialAsset(false));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get the Zapworks provider for uploads
  const zapworksProvider = registry.get('zapworks') as ZapworksProvider | undefined;

  // Centralized toast logic
  const toastsRef = useRef<Components.ZprToasts & HTMLElement>(null);
  const addToast = useCallback(({ message, type }: { message: string; type: string }) => {
    toastsRef.current?.addToast(message, undefined, type as ToastType);
  }, []);

  const { postSystemComment } = useEchoContext();

  const { deleteAsset, deleteMultiple } = useDeleteAssets({
    registry,
    workspaceId,
    onSuccess: (successMessage) => {
      addToast({ message: successMessage, type: 'success' });
      setRefreshKey(k => k + 1);
    },
    onError: (error) => {
      addToast({ message: error.message || 'Failed to delete asset', type: 'error' });
    },
    onMovedToBin: (assetId) => {
      postSystemComment(assetId, movedToBinBody(), { coalesceKey: 'bin' });
    },
  });

  // Use parent-provided uploadQueue or create local one
  const uploadQueue = useUploadQueue({
    provider: zapworksProvider!,
    workspaceId,
    onUploadComplete: (assetId) => {
      // setRefreshKey(k => k + 1);
    },
    onUploadError: (fileName, error) => {
      console.error(`Upload failed for ${fileName}:`, error);
    },
    onNewVersionUploaded: (assetId) => {
      postSystemComment(assetId, versionUploadedBody(), { coalesceKey: 'version-upload' });
    },
  });

  useEffect(() => {
    // Only set upload queue  in global store if separate uploader component used (i.e. when uploaderContainer is not provided).
    if (uploaderContainer) return;
    mediaLibraryStore.setState({ uploadQueue });
  }, [uploadQueue])

  // When using an external upload queue, watch for newly completed uploads and refresh
  // The API may take a moment to index new files, so we refresh with a delay and again after a longer wait
  const completedCount = uploadQueue.uploads.filter(u => u.status === 'completed').length;
  const prevCompletedCountRef = useRef(completedCount);
  useEffect(() => {
    if (completedCount > prevCompletedCountRef.current) {
      // Refresh after a short delay to give the API time to index
      const t1 = setTimeout(() => setRefreshKey(k => k + 1), 1000);
      prevCompletedCountRef.current = completedCount;
      return () => { clearTimeout(t1); };
    }
    prevCompletedCountRef.current = completedCount;
  }, [completedCount]);


  useEffect(() => {
    zwApiClient.checkWorkspaceUserPermissions(workspaceId, {zml: [ZmlPermissions.CanAssignLabels, ZmlPermissions.CanChangeStatus, ZmlPermissions.CanDeleteAsset, ZmlPermissions.CanEditLabels, ZmlPermissions.CanPublish, ZmlPermissions.CanUploadVersion]}
    ).then(permissions => {
      setZmlPermission(permissions.zml);
    }).catch(err => {
      console.error('Failed to fetch user permissions:', err);
    });
  }, []);

  const { statuses } = useWorkflowStatuses(zapworksProvider, workspaceId);

  const handleUpload = useCallback((files: FileList) => {
    if (zapworksProvider && files.length > 0) {
      // Add files to upload queue (don't await - let it run async)
      // mediaType can be undefined/null in cms mode
      uploadQueue.addFiles(Array.from(files));
    }
  }, [zapworksProvider, uploadQueue, activeSource]);

  const handleUploadNewVersion = async (asset: IAsset, file: File) => {
    const prevFile = await asset.getZmlFile?.();
    if (prevFile) {
      uploadQueue.addFiles([file], undefined, null, prevFile);
    }
  }

  const handleUploadPopupFilesSelect = useCallback((files: File[]) => {
    setUploadPopupFiles(files);
  }, []);

  const handleUploadPopupUpload = useCallback((files: File[], status: string) => {
    if (zapworksProvider && files.length > 0) {
      uploadQueue.addFiles(files, undefined, status);
      setShowUploadPopup(false);
      setUploadPopupFiles([]);
    }
  }, [zapworksProvider, uploadQueue, activeSource]);

  const handleGoToAsset = useCallback((assetId: string) => {
    zapworksProvider?.getAssetById(assetId, workspaceId).then(asset => {
      if (asset) {
        setSelectedAsset(asset);
        setCurrentView('asset');
      } else {
        addToast({ message: 'Asset not found', type: 'error' });
      }
    }).catch(err => {
      console.error('Failed to fetch asset:', err);
      addToast({ message: 'Failed to fetch asset', type: 'error' });
    });
  }, [workspaceId]);

  const handleOpenMissingFilesDialog = useCallback((uploadId: string) => {
    setActiveGltfUploadId(uploadId);
    setCurrentView('gltf-missing-files');
  }, []);

  useEffect(() => {
    // Only register callbacks in global store if separate uploader component used (i.e. when uploaderContainer is not provided).
    if (uploaderContainer) return;
    mediaLibraryActions.registerAddMissingFilesCallback(handleOpenMissingFilesDialog);
    mediaLibraryActions.registerGoToFileCallback(handleGoToAsset);

    return () => mediaLibraryActions.clearCallbacks();
  }, [workspaceId]);

  useImperativeHandle(_forwardedRef, () => ({
    openMissingFilesDialog: handleOpenMissingFilesDialog,
  }), [handleOpenMissingFilesDialog]);

  // Handle media type selection from home view (tool mode)
  const handleMediaTypeSelect = (mediaType: MediaType) => {
    setSelectedMediaType(mediaType);
    setCurrentView('browser');
  };

  // Handle back navigation from browser
  const handleBack = () => {
    setCurrentView('home');
    if (!initialMediaType) setSelectedMediaType(null);
    setActiveSource('user-library');
  };

  const handleAssetSelect = (asset: IAsset) => {
    if (onSelect) {
      onSelect(asset);
    }
  };

  // Handle "View all" navigation from home view
  const handleViewAll = (section: 'workspace' | 'personal') => {
    setActiveSource(section === 'workspace' ? 'workspace-library' : 'user-library');
    if (!initialMediaType) setSelectedMediaType(null); // Show all types in browser (unless locked by initialMediaType)
    setCurrentView('browser');
    // searchQuery is already preserved in parent state
  };

  // Handle source selection from home view sidebar
  const handleSourceSelect = (source: AssetSource) => {
    setActiveSource(source);
    if (!initialMediaType) setSelectedMediaType(null); // Show all types in browser (unless locked by initialMediaType)
    if (source === 'explore') {
      setCurrentView('explore');
    } else {
      setCurrentView('browser');
    }
  };

  const handleExploreMediaTypeButtonClick = (mediaType: MediaType) => {
    setSelectedMediaType(mediaType);
    setCurrentView('explore');
  }

  const handleToggleMultiSelect = useCallback(() => {
    if (!initialEnableMultiSelect) return;
    setEnableMultiSelect((prev) => !prev);
  }, [initialEnableMultiSelect]);

  const handleEditAsset = (asset: IAsset) => {
    setSelectedAsset(asset);
    setCurrentView('asset');
  };

  const handleWorkflowStatusChange = (newStatus: string) => {
    const currentStatusUid = (selectedAsset?.getMetadata().workflowStatusId as string | undefined) ?? '';
    if (newStatus === currentStatusUid) return;
    const status = statuses.find(s => s.uid === newStatus);
    const id = status?.uid || '';
    const assetWorkspaceId = selectedAsset?.getWorkspaceId() ?? workspaceId;
    try {
      zapworksProvider?.updateWorkflowStatus(selectedAsset!.getId(), id, assetWorkspaceId);
      addToast({ message: 'Workflow status updated', type: 'success' });
      if (selectedAsset) {
        postSystemComment(
          selectedAsset.getId(),
          workflowStatusChangedBody(status?.name ?? null, currentStatusUid),
          {
            coalesceKey: 'workflow-status',
            rewriteBody: (prior) => {
              // Preserve the very first "from" status across a burst, and detect
              // net no-op: A→B then B→A within the window cancels out.
              const priorOriginal = (prior.data?.originalStatusUid as string | null | undefined) ?? currentStatusUid;
              if ((priorOriginal ?? '') === id) return null;
              return workflowStatusChangedBody(status?.name ?? null, priorOriginal);
            },
          },
        );
      }
    } catch (err) {
      addToast({ message: 'Failed to update workflow status', type: 'error' });
    }
  };

  const handleRestoreAsset = async (assetId: string, asset?: IAsset) => {
    const assetWorkspaceId = asset?.getWorkspaceId() ?? workspaceId;
    try {
      await zapworksProvider?.restore(assetId, assetWorkspaceId);
      addToast({ message: `${assetId} was restored to Workspace.`, type: 'success' });
      postSystemComment(assetId, restoredFromBinBody(), {
        mode: 'cancel',
        cancelKey: 'bin',
      });
      setRefreshKey(k => k + 1);
    } catch (err) {
      addToast({ message: 'Fail to restore', type: 'error' })
    }
  }

  const handleUpdateAssetLabels = async (asset: IAsset, labels: ILabel[]) => {
    try {
      const assetId = asset.getId();
      const assetWorkspaceId = asset.getWorkspaceId() ?? workspaceId;

      const previous = (asset.getMetadata().labels as ILabel[] | undefined) ?? [];
      const previousUids = new Set(previous.map(l => l.uid));
      const nextUids = new Set(labels.map(l => l.uid));
      const addedNames = labels.filter(l => !previousUids.has(l.uid)).map(l => l.name);
      const removedNames = previous.filter(l => !nextUids.has(l.uid)).map(l => l.name);

      await zapworksProvider?.updateFileLabels(assetId, labels.map(l => l.uid), assetWorkspaceId);
      setRefreshKey(k => k + 1);
      addToast({ message: 'Labels updated successfully', type: 'success' });

      const labelCoalesce = {
        coalesceKey: 'labels',
        rewriteBody: (prior: SystemMessage) => {
          const priorAdded = (prior.data?.added as string[] | undefined) ?? [];
          const priorRemoved = (prior.data?.removed as string[] | undefined) ?? [];
          // Fold the burst: anything added then removed (or removed then added) cancels.
          const allAdded = new Set([...priorAdded, ...addedNames]);
          const allRemoved = new Set([...priorRemoved, ...removedNames]);
          const finalAdded = [...allAdded].filter(n => !allRemoved.has(n));
          const finalRemoved = [...allRemoved].filter(n => !allAdded.has(n));
          if (!finalAdded.length && !finalRemoved.length) return null;
          if (finalAdded.length && finalRemoved.length) return labelsChangedBody(finalAdded, finalRemoved);
          if (finalAdded.length) return labelsAddedBody(finalAdded);
          return labelsRemovedBody(finalRemoved);
        },
      };
      if (addedNames.length > 0 && removedNames.length > 0) {
        postSystemComment(assetId, labelsChangedBody(addedNames, removedNames), labelCoalesce);
      } else if (addedNames.length > 0) {
        postSystemComment(assetId, labelsAddedBody(addedNames), labelCoalesce);
      } else if (removedNames.length > 0) {
        postSystemComment(assetId, labelsRemovedBody(removedNames), labelCoalesce);
      }
    } catch (error) {
      console.error('Failed to update asset labels:', error);
      setRefreshKey(k => k + 1);
      addToast({ message: 'Failed to update labels', type: 'error' });
      throw error;
    }
  };

  const handleCreateLabel = async (name: string) => {
    addToast({ message: `Label '${name}' created successfully`, type: 'success' });
  }

  const handleDeleteLabel = async (_uid: string) => {
    addToast({ message: 'Label deleted successfully', type: 'success' });
  }

  const handleEditLabel = async (_uid: string, newName: string) => {
    addToast({ message: `Label edited successfully`, type: 'success' });
  }

  const handleViewLabelAnalytics = (id: string) => {
    window.location.assign(`/zap-alytics/group/uid/${id}/`)
  }

  const handleAddToProject = (asset: IAsset) => {
    if (onAddToProject) {
      onAddToProject(asset);
    }
  };

  const handleAddToWorkspaceAssets = async (assets: IAsset[]) => {
    if (!zapworksProvider) return;
    const publicAssets = assets.filter(asset => asset.getMetadata().access === 'public');
    const otherAssetIds = assets
      .filter(asset => asset.getMetadata().access !== 'public')
      .map(asset => asset.getId());
    try {
      await Promise.all([
        ...publicAssets.map(asset => zapworksProvider.copyFileToWorkspace(workspaceId, asset.getId())),
        ...(otherAssetIds.length > 0 ? [zapworksProvider.moveFilesToWorkspace(workspaceId, otherAssetIds)] : []),
      ]);
      addToast({ message: 'Asset added to Workspace Assets', type: 'success' });
      setRefreshKey(k => k + 1);
    } catch (error) {
      console.error('Failed to add asset to Workspace Assets:', error);
      addToast({ message: 'Failed to add asset to Workspace Assets', type: 'error' });
    }
  };

  return (
    <AssetActionsProvider value={{ zapworksProvider, workspaceId, onUpdateLabels: handleUpdateAssetLabels, onCreateLabel: handleCreateLabel, onDeleteLabel: handleDeleteLabel, onEditLabel: handleEditLabel, onViewLabelAnalytics: handleViewLabelAnalytics, onAddToProject: handleAddToProject, onAddToWorkspaceAssets: handleAddToWorkspaceAssets }}>
        <ModeProvider premium={premium && activeSource !== 'user-library'} isToolMode={mode === 'tool'} useNestleFeatures={useNestleFeatures} permissions={zmlPermission}>
          {currentView === 'selection' && (
            <MediaTypeSelection
              enabledMediaTypes={enabledMediaTypes}
              onSelectMediaType={handleMediaTypeSelect}
            />
          )}

          {currentView === 'home' && (
            <HomeView
              registry={registry}
              mediaType={selectedMediaType}
              workspaceId={workspaceId}
              handleUpload={handleUpload}
              enabledMediaTypes={enabledMediaTypes}
              showExploreRow={mode === 'tool'}
              showSidebar={mode === 'tool'}
              onSelect={handleAssetSelect}
              onViewAll={handleViewAll}
              onSelectMediaType={handleExploreMediaTypeButtonClick}
              onSourceSelect={handleSourceSelect}
              enableMultiSelect={enableMultiSelect}
              showMultiSelectToggle={initialEnableMultiSelect}
              onToggleMultiSelect={handleToggleMultiSelect}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              filters={filters}
              onFiltersChange={setFilters}
              onDelete={deleteAsset}
              onDeleteMultiple={deleteMultiple}
              onOpenPremiumUploadModal={() => setShowUploadPopup(true)}
              refreshKey={refreshKey}
            />
          )}

          {currentView === 'browser' && (
            <AssetBrowserView
              registry={registry}
              mediaType={selectedMediaType}
              enabledMediaTypes={enabledMediaTypes}
              workspaceId={workspaceId}
              enableNav={mode === 'tool'}
              enableMultiSelect={enableMultiSelect}
              showMultiSelectToggle={initialEnableMultiSelect}
              onToggleMultiSelect={handleToggleMultiSelect}
              onSelect={handleAssetSelect}
              onBack={handleBack}
              onSourceSelect={handleSourceSelect}
              initialSource={activeSource}
              handleUpload={handleUpload}
              onEdit={handleEditAsset}
              initialSearchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              filters={filters}
              onFiltersChange={setFilters}
              onRestore={handleRestoreAsset}
              onDelete={deleteAsset}
              onDeleteMultiple={deleteMultiple}
              refreshKey={refreshKey}
              onOpenPremiumUploadModal={() => setShowUploadPopup(true)}
            />
          )}

          {mode === 'tool' && currentView === 'explore' && (
            <ExploreView
              registry={registry}
              enabledMediaTypes={enabledMediaTypes}
              mediaType={selectedMediaType}
              workspaceId={workspaceId}
              onBack={handleBack}
              onSelect={handleAssetSelect}
              enableMultiSelect={enableMultiSelect}
              showMultiSelectToggle={initialEnableMultiSelect}
              onToggleMultiSelect={handleToggleMultiSelect}
              handleUpload={handleUpload}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              filters={filters}
              onFiltersChange={setFilters}
              onDelete={deleteAsset}
              onDeleteMultiple={deleteMultiple}
              onSourceSelect={handleSourceSelect}
              onOpenPremiumUploadModal={() => setShowUploadPopup(true)}
              refreshKey={refreshKey}
            />
          )}

          {currentView === 'asset' && isFetchingInitialAsset && (
            <div className="media-library__loading" aria-busy="true" aria-label="Loading asset" />
          )}

          {currentView === 'asset' && selectedAsset && (
            <AssetView
              zwApiClient={zwApiClient}
              registry={registry}
              asset={selectedAsset as IAsset}
              activeSource={activeSource}
              onBack={() => {
                setCurrentView('browser');
                setSelectedAsset(null);
              }}
              onUploadNewVersion={handleUploadNewVersion}
              onStatusChange={handleWorkflowStatusChange}
              onDelete={deleteAsset}
              onRestore={handleRestoreAsset}
              acceptedFileTypes={[selectedAsset.getMediaType()]}
              onViewAnalytics={() => setCurrentView('analytics')}
              onPublicLinkCreated={() => addToast({ message: 'This asset is now publicly accessible.', type: 'success' })}
              onPublicLinkError={(message) => addToast({ message, type: 'error' })}
              key={refreshKey}
              refreshKey={refreshKey}
              environmentImage={environmentImageUrl}
            />
          )}

          {currentView === 'analytics' && selectedAsset && (
            <Suspense fallback={null}>
              <AnalyticsView
                asset={selectedAsset as IAsset}
                onBack={() => setCurrentView('asset')}
              />
            </Suspense>
          )}

          {premium && showUploadPopup && (
            <UploadPopup
              isOpen={showUploadPopup}
              onClose={() => {
                setShowUploadPopup(false);
                setUploadPopupFiles([]);
              }}
              onUpload={handleUploadPopupUpload}
              onFilesSelect={handleUploadPopupFilesSelect}
              isLoading={uploadQueue.uploads.some(upload => upload.status === 'uploading')}
              selectedFiles={uploadPopupFiles}
              acceptedFileTypes={enabledMediaTypes}
              workflowStatuses={statuses}
            />
          )}

          {currentView === 'gltf-missing-files' && activeGltfUploadId && (() => {
            const upload = uploadQueue.uploads.find(u => u.id === activeGltfUploadId);
            return upload?.missingFiles ? (
              <GltfMissingFilesView
                gltfFileName={upload.file.name}
                missingFiles={upload.missingFiles}
                onSubmit={(files) => {
                  uploadQueue.addMissingFiles(activeGltfUploadId, files);
                  setActiveGltfUploadId(null);
                  setCurrentView('browser');
                }}
                onClose={() => {
                  setActiveGltfUploadId(null);
                  setCurrentView('browser');
                }}
              />
            ) : null;
          })()}

          <ZprToasts ref={toastsRef} />
          {uploaderContainer && uploadQueue.uploads.length > 0 && createPortal(
            <Uploader
              uploads={uploadQueue.uploads}
              //onCancel={uploadQueue.cancelUpload}
              onRetry={uploadQueue.retryUpload}
              onRemove={uploadQueue.removeUpload}
              showGoToFile={true}
              onGoToFile={handleGoToAsset}
              onAddMissingFiles={handleOpenMissingFilesDialog}
              theme={theme}
            />,
            uploaderContainer
          )}
        </ModeProvider>
      </AssetActionsProvider>
  );
};

export const MediaLibrary = forwardRef<MediaLibraryRef, MediaLibraryProps>((props, ref) => {
  const { zwApiClient, workspaceId, theme = 'plain', className = '' } = props;
  const libraryClasses = ['media-library', className].filter(Boolean).join(' ');
  return (
    <ThemeProvider theme={theme} className={libraryClasses}>
      <EchoProvider zwApiClient={zwApiClient} workspaceId={workspaceId}>
        <MediaLibraryInner {...props} _forwardedRef={ref} />
      </EchoProvider>
    </ThemeProvider>
  );
});
